import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  crmDeals,
  salesOutcomes,
  salesPilot,
  salesProofLinesDemo,
  salesProofLinesLive,
  type OutcomesFigure,
  type SalesPilotResponse,
} from '@aithora/shared';
import { useSession } from '../../../session/SessionContext';
import { useQuery } from '../../../useQuery';
import { ErrorNote } from '../../../ErrorNote';
import { useSales } from '../context';
import { ck, hov, sx } from '../dc';
import { copyText } from '../kit/model';
import { tc } from '../targeting/model';
import { LOCKED, PORTFOLIO, PROOF } from './catalog';
import { PilotOutcomes } from './PilotOutcomes';

/** How long "Copied with its label" stays up (design _ocT). */
export const COPIED_MS = 1800;
/** Open deals read to find the ones at a Pilot stage. */
const DEALS_LIMIT = 200;
/** Pilot clocks loaded at most (one GET /sales/pilots/:dealId each). */
const PILOTS_MAX = 20;

// design salesVals Outcomes styles (L20440-20451)
const ink = 'font-size:16px;font-weight:800;color:#22272E;font-variant-numeric:tabular-nums;white-space:nowrap;';
const good = ink.replace('#22272E', '#1F6B52');
const none = 'font-size:14px;font-weight:600;color:#54606C;font-style:italic;white-space:nowrap;';
const big = 'font-size:22px;font-weight:800;color:#22272E;font-variant-numeric:tabular-nums;line-height:1.1;';
const bigGood = big.replace('#22272E', '#1F6B52');
const bigNone = 'font-size:16px;font-weight:600;color:#54606C;font-style:italic;line-height:1.9;';
const mb = (on: boolean) =>
  'display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;' + (on ? 'background:#22272E;color:#F7F3EA;' : 'color:#54606C;');
const sp = (arr: number[], col?: string) =>
  arr.map((v) => ({ st: 'display:block;width:3px;border-radius:1px;height:' + Math.max(2, Math.round(v * 16)) + 'px;background:' + (col || '#2F8A6F') + ';opacity:.85;' }));
const stS = (st: string) =>
  'font-size:14px;font-weight:800;letter-spacing:.06em;border-radius:3px;padding:1px 5px;' +
  (st === 'Live' ? 'color:#1F6B52;background:rgba(47,138,111,.13);' : st === 'Pilot' ? 'color:#264C7E;background:rgba(79,138,214,.14);' : 'color:#8A5A00;background:rgba(165,86,26,.13);');

/** A pilot deal's row (design ocPilots, L20979-20984), from its server clock. */
export function pilotRow(name: string, clock: SalesPilotResponse['clock']) {
  const day = Math.min(90, clock.day ?? 0);
  const ph = day < 30 ? 'Collecting · nothing to show yet' : day < 90 ? 'Early read · not final' : 'Pilot result ready';
  return {
    name: tc(name),
    day: 'Day ' + day + ' of 90',
    ph,
    gng: 'Go / no-go ' + (clock.go_no_go_date ?? '–'),
    bar:
      'display:block;height:100%;border-radius:3px;background:' +
      (day < 30 ? '#54606C' : day < 90 ? '#4F8AD6' : '#2F8A6F') +
      ';width:' +
      Math.round((day / 90) * 100) +
      '%;transform-origin:left;animation:growW .6s cubic-bezier(.22,.61,.36,1) both;',
  };
}

/**
 * Outcomes (design T6603-6679, salesVals L20438-20531 and L21011-21019). Live: each
 * figure in its designed "At first go-live" / "Not measured yet" state until the server
 * measures it, the pilots at a Pilot stage with their 90-day clocks, and the real proof
 * lines the server allows. Demo: the 90-Day Pilot Report's demonstration set, served
 * with its label, which every copied line carries.
 */
export function OutcomesTab() {
  const { client, session } = useSession();
  const { params, setParam, openPrep } = useSales();
  const navigate = useNavigate();
  const demo = params.get('ocMode') === 'demo';
  const mode = demo ? 'demo' : 'live';

  const figQ = useQuery((signal) => salesOutcomes(client, { query: { mode }, signal }), [client, session?.token, mode]);
  const liveLinesQ = useQuery((signal) => salesProofLinesLive(client, { query: { mode: 'live' }, signal }), [client, session?.token], { enabled: !demo });
  const demoLinesQ = useQuery((signal) => salesProofLinesDemo(client, { query: { mode: 'demo' }, signal }), [client, session?.token], { enabled: demo });
  const pilotsQ = useQuery(
    async (signal) => {
      const r = await crmDeals(client, { query: { status: 'open', limit: DEALS_LIMIT }, signal });
      const atPilot = r.deals.filter((d) => /pilot/i.test(d.stage_name ?? '')).slice(0, PILOTS_MAX);
      const clocks = await Promise.all(atPilot.map((d) => salesPilot(client, { params: { dealId: d.id }, signal })));
      return atPilot.map((d, i) => ({ deal: d, pilot: clocks[i] })).filter((x) => x.pilot.clock.day != null);
    },
    [client, session?.token],
    { enabled: !demo },
  );

  // The figures of the mode on screen; a mode that failed to load has none.
  const figs = figQ.data?.mode === mode ? figQ.data : null;
  const isDemoData = demo && !!figs && figs.real === false;
  const label = (demo ? (figs?.label ?? demoLinesQ.data?.label) : null) ?? null;

  const [proofCopied, setProofCopied] = useState(false);
  const [lineCopied, setLineCopied] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const withLabel = (line: string, lab: string | null) => (lab ? line + ' (' + lab + ')' : line);
  const copyProof = (line: string) => {
    copyText(withLabel(line, label));
    setProofCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setProofCopied(false), COPIED_MS);
  };

  const row = (key: string, k: string, def: string, isNew: boolean | undefined) => {
    const f: OutcomesFigure | undefined = figs?.figures[key];
    const ok = !!f;
    return {
      k,
      v: ok ? f.value : isNew ? 'Not measured yet' : 'At first go-live',
      sub: ok ? (f.sub ?? '') : def,
      tip: ok ? (f.sub ?? '') : def,
      vStyle: !ok ? none : f.good ? good : ink,
      demo: ok && isDemoData,
      spark: ok && f.spark ? sp(f.spark.slice(-7), f.spark_good ? '#1F6B52' : undefined) : [],
      canCopy: ok && !!f.line,
      onCopy: () => f?.line && copyProof(f.line),
      onOpen: () => navigate('/performance'),
    };
  };
  const livePilots = useMemo(() => (pilotsQ.data ?? []).map((x) => pilotRow(x.deal.facility_name ?? x.deal.name, x.pilot.clock)), [pilotsQ.data]);
  const liveLines = liveLinesQ.data?.lines ?? [];
  const liveOnAny = !!figs?.portfolio.live_or_pilot && figs.portfolio.live_or_pilot.value !== '0';
  const byFac = demo ? (figs?.by_facility ?? []) : [];

  const v = {
    ocIsDemo: demo,
    ocIsLive: !demo,
    ocNotDemo: !demo,
    ocSub: demo
      ? 'From the 90-Day Pilot Report.'
      : liveOnAny
        ? 'Each figure fills in from real facilities, and rates appear once the sample is large enough.'
        : 'No facility is live yet. Each figure fills in from real facilities, and rates appear once the sample is large enough.',
    ocLiveBtn: mb(!demo),
    ocDemoBtn: mb(demo),
    ocGoLive: () => setParam('ocMode', null),
    ocGoDemo: () => setParam('ocMode', 'demo'),
    ocProofCopied: proofCopied,
    ocPrep: () => openPrep(null),
    ocPort: PORTFOLIO.map(([key, k, def]) => {
      const f = figs?.portfolio[key];
      if (demo && f) return { k, v: f.value, sub: f.sub ?? '', vStyle: f.good ? bigGood : big, demo: isDemoData };
      return { k, v: f ? f.value : 'At first go-live', sub: def, vStyle: f ? big : bigNone, demo: false };
    }),
    ocProof: PROOF.map((s) => ({ title: s.title, q: s.q, rows: s.rows.map((r) => row(r.key, r.k, r.def, r.isNew)) })),
    ocNoPilots: !pilotsQ.loading && !pilotsQ.error && livePilots.length === 0,
    ocPilots: livePilots,
    ocDemo: (demoLinesQ.data?.lines ?? []).map((d) => ({
      t: d.text,
      onCopy: () => {
        copyText(withLabel(d.text, demoLinesQ.data?.label ?? null));
        setLineCopied(d.text);
      },
      lab: lineCopied === d.text ? 'Copied' : 'Copy',
    })),
    ocLive: liveLines.map((l, i) => {
      const t =
        (l.facility_name ? tc(l.facility_name) + ': ' : '') +
        l.metric +
        ' ' +
        (l.baseline != null ? l.baseline + (l.unit ? ' ' + l.unit : '') + ' → ' : '') +
        (l.current != null ? l.current + (l.unit ? ' ' + l.unit : '') : '–') +
        (l.day != null ? ', day ' + l.day : '');
      const key = 'live' + i;
      return {
        t,
        label: l.label,
        onCopy: () => {
          copyText(withLabel(t, l.label));
          setLineCopied(key);
        },
        lab: lineCopied === key ? 'Copied' : 'Copy',
      };
    }),
    ocFacEmpty: demo && !!figs && byFac.length === 0,
    ocByFac: byFac.map((f) => ({
      n: f.name,
      st: f.status,
      stS: stS(f.status),
      d: f.days != null ? f.days + ' d' : '–',
      r: f.residents != null ? String(f.residents) : '–',
      x: f.collected_month != null ? '$' + Math.round(f.collected_month).toLocaleString('en-US') + ' / mo' : '–',
      sub: f.sub,
    })),
    ocLocked: LOCKED.map(([k, val]) => ({ k, v: val })),
  };

  return (
    <div data-testid="sales-outcomes" style={sx('display:flex;flex-direction:column;gap:10px;min-height:calc(100vh - 150px);')}>
      <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
        <span style={sx('font-size:17px;font-weight:800;color:#22272E;')}>What Aithora delivers</span>{' '}
        {!!v.ocIsDemo && (
          <span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;color:#8A5A00;background:rgba(165,86,26,.13);border-radius:3px;padding:2px 5px;')}>PILOT REPORT DATA</span>
        )}{' '}
        <span style={sx('font-size:16px;color:#54606C;min-width:0;text-wrap:pretty;')}>
          <span>{v.ocSub}</span>
        </span>{' '}
        <span style={sx('flex:1;')} />{' '}
        <span role="tablist" aria-label="Outcomes mode" style={sx('display:inline-flex;gap:2px;padding:2px;border-radius:8px;background:rgba(34,39,46,.06);')}>
          <a href="#" role="tab" aria-selected={!demo} onClick={ck(v.ocGoLive)} style={sx(v.ocLiveBtn)}>
            Live
          </a>
          <a href="#" role="tab" aria-selected={demo} onClick={ck(v.ocGoDemo)} style={sx(v.ocDemoBtn)}>
            Pilot report
          </a>
        </span>{' '}
        {!!v.ocProofCopied && <span style={sx('font-size:14px;font-weight:700;color:#1F6B52;')}>Copied with its label</span>}{' '}
        <a href="#" onClick={ck(v.ocPrep)} style={sx('display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;background:#22272E;color:#F7F3EA;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;')}>
          Prep for a meeting
        </a>
      </div>{' '}
      <div data-testid="oc-port" style={sx('display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:0;border-top:1px solid rgba(34,39,46,.14);border-bottom:1px solid rgba(34,39,46,.14);')}>
        {v.ocPort.map((p, p_i) => (
          <Fragment key={p_i}>
            <div style={sx('display:flex;flex-direction:column;gap:3px;padding:9px 12px 9px 2px;min-width:0;')}>
              <span style={sx('font-size:14px;letter-spacing:.08em;text-transform:uppercase;color:#54606C;font-weight:700;overflow-wrap:break-word;')} title={p.k}>
                <span>{p.k}</span>
              </span>{' '}
              <span style={sx('display:flex;align-items:center;gap:6px;')}>
                <span style={sx(p.vStyle)}>
                  <span>{p.v}</span>
                </span>
                {!!p.demo && (
                  <span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;color:#8A5A00;background:rgba(165,86,26,.13);border-radius:3px;padding:1px 4px;')}>PILOT</span>
                )}
              </span>{' '}
              <span style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')} title={p.sub}>
                <span>{p.sub}</span>
              </span>
            </div>
          </Fragment>
        ))}
      </div>{' '}
      {!!figQ.error && figQ.error.status !== 404 && <ErrorNote error={figQ.error} onRetry={figQ.reload} compact />}
      <div data-testid="oc-proof" style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:4px 22px;flex:1 1 auto;align-content:start;')}>
        {v.ocProof.map((pn, pn_i) => (
          <Fragment key={pn_i}>
            <div style={sx('display:flex;flex-direction:column;min-width:0;')}>
              <div style={sx('display:flex;align-items:baseline;gap:8px;padding:6px 0 5px;border-bottom:1px solid rgba(34,39,46,.14);')}>
                <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>
                  <span>{pn.title}</span>
                </span>{' '}
                <span style={sx('font-size:14px;color:#54606C;min-width:0;overflow-wrap:break-word;')}>
                  <span>{pn.q}</span>
                </span>
              </div>{' '}
              {pn.rows.map((rw, rw_i) => (
                <Fragment key={rw_i}>
                  <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) auto 22px;align-items:center;gap:8px;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.07);')} className={hov('background:rgba(34,39,46,.03);')}>
                    <a href="#" onClick={ck(rw.onOpen)} title={rw.tip} style={sx('display:flex;flex-direction:column;gap:2px;min-width:0;text-decoration:none;color:inherit;')}>
                      <span style={sx('font-size:16px;font-weight:600;color:#22272E;overflow-wrap:break-word;')}>
                        <span>{rw.k}</span>
                      </span>{' '}
                      <span style={sx('font-size:14px;color:#54606C;line-height:1.35;overflow-wrap:break-word;text-wrap:pretty;')}>
                        <span>{rw.sub}</span>
                      </span>
                    </a>{' '}
                    <span style={sx('display:flex;align-items:center;gap:8px;')}>
                      <span style={sx('display:flex;align-items:flex-end;gap:2px;height:16px;')}>
                        {rw.spark.map((hb, hb_i) => (
                          <Fragment key={hb_i}>
                            <span style={sx(hb.st)} />
                          </Fragment>
                        ))}
                      </span>{' '}
                      <span style={sx('display:flex;flex-direction:column;align-items:flex-end;gap:1px;')}>
                        <span style={sx(rw.vStyle)}>
                          <span>{rw.v}</span>
                        </span>
                        {!!rw.demo && <span style={sx('font-size:14px;font-weight:800;letter-spacing:.08em;color:#8A5A00;line-height:1;')}>PILOT</span>}
                      </span>
                    </span>{' '}
                    {!!rw.canCopy && (
                      <a href="#" onClick={ck(rw.onCopy)} title="Copy as a proof line" aria-label="Copy as a proof line" style={sx('display:flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:5px;color:#54606C;text-decoration:none;')} className={hov('background:rgba(34,39,46,.08);color:#22272E;')}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="12" height="12" rx="2" />
                          <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                        </svg>
                      </a>
                    )}
                  </div>
                </Fragment>
              ))}
            </div>
          </Fragment>
        ))}
      </div>{' '}
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:14px 18px;margin-top:2px;padding-top:6px;border-top:1px solid rgba(34,39,46,.12);')}>
        {' '}
        {!!v.ocIsLive && (
          <div data-testid="oc-pilots" style={sx('display:flex;flex-direction:column;gap:7px;min-width:0;')}>
            <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Pilots and onboarding</div>{' '}
            {!!pilotsQ.error && <ErrorNote error={pilotsQ.error} onRetry={pilotsQ.reload} compact />}
            {!!v.ocNoPilots && (
              <div style={sx('font-size:16px;color:#54606C;line-height:1.5;')}>
                No pilot running yet. A deal moved to Pilot in the CRM starts its 90-day clock here, with its onboarding: consent, devices, EHR and go-live date.
              </div>
            )}{' '}
            {v.ocPilots.map((p, p_i) => (
              <Fragment key={p_i}>
                <div style={sx('display:flex;flex-direction:column;gap:4px;')}>
                  <div style={sx('display:flex;align-items:center;gap:8px;')}>
                    <span style={sx('font-size:16px;font-weight:700;flex:1 1 auto;min-width:0;overflow-wrap:break-word;')}>
                      <span>{p.name}</span>
                    </span>
                    <span style={sx('font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;')}>
                      <span>{p.day}</span>
                    </span>
                  </div>{' '}
                  <span style={sx('display:block;height:6px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;')}>
                    <span style={sx(p.bar)} />
                  </span>{' '}
                  <div style={sx('display:flex;gap:8px;font-size:14px;color:#54606C;')}>
                    <span>
                      <span>{p.ph}</span>
                    </span>
                    <span style={sx('flex:1;')} />
                    <span>
                      <span>{p.gng}</span>
                    </span>
                  </div>
                  {pilotsQ.data?.[p_i] && <PilotOutcomes dealId={pilotsQ.data[p_i].deal.id} />}
                </div>
              </Fragment>
            ))}
          </div>
        )}{' '}
        <div data-testid="oc-lines" style={sx('display:flex;flex-direction:column;gap:7px;min-width:0;')}>
          <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Proof lines a rep can use</div>{' '}
          {!!v.ocIsDemo && (
            <div style={sx('display:flex;flex-direction:column;gap:7px;min-width:0;')}>
              <span style={sx('align-self:flex-start;font-size:14px;font-weight:800;letter-spacing:.07em;color:#8A5A00;background:rgba(165,86,26,.13);border-radius:3px;padding:2px 5px;')}>
                PILOT REPORT &#183; SYNTHETIC RESIDENTS
              </span>{' '}
              {!!demoLinesQ.error && <ErrorNote error={demoLinesQ.error} onRetry={demoLinesQ.reload} compact />}
              {v.ocDemo.map((d, d_i) => (
                <Fragment key={d_i}>
                  <div style={sx('display:flex;align-items:center;gap:8px;')}>
                    <span style={sx('font-size:16px;color:#22272E;flex:1 1 auto;min-width:0;')}>
                      <span>{d.t}</span>
                    </span>
                    <a href="#" onClick={ck(d.onCopy)} style={sx('font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}>
                      <span>{d.lab}</span>
                    </a>
                  </div>
                </Fragment>
              ))}{' '}
              <div style={sx('font-size:14px;color:#54606C;line-height:1.45;')}>Copied lines carry the label with them.</div>
            </div>
          )}
          {!!v.ocNotDemo &&
            (v.ocLive.length ? (
              <div style={sx('display:flex;flex-direction:column;gap:7px;min-width:0;')}>
                {v.ocLive.map((d, d_i) => (
                  <Fragment key={d_i}>
                    <div style={sx('display:flex;align-items:center;gap:8px;')}>
                      <span style={sx('font-size:16px;color:#22272E;flex:1 1 auto;min-width:0;')}>
                        <span>{d.t}</span> <span style={sx('font-size:14px;color:#54606C;')}>{d.label}</span>
                      </span>
                      <a href="#" onClick={ck(d.onCopy)} style={sx('font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}>
                        <span>{d.lab}</span>
                      </a>
                    </div>
                  </Fragment>
                ))}
                <div style={sx('font-size:14px;color:#54606C;line-height:1.45;')}>Copied lines carry the label with them.</div>
              </div>
            ) : liveLinesQ.error ? (
              <ErrorNote error={liveLinesQ.error} onRetry={liveLinesQ.reload} compact />
            ) : (
              <div style={sx('font-size:16px;color:#54606C;line-height:1.5;')}>
                No proof lines yet. They appear here once a pilot passes day 90 on the metrics agreed in Week 1. Switch to Demo to see how they read.
              </div>
            ))}
        </div>{' '}
        {!!v.ocIsDemo && (
          <div data-testid="oc-byfac" style={sx('display:flex;flex-direction:column;gap:5px;min-width:0;')}>
            <div style={sx('display:flex;align-items:baseline;gap:8px;')}>
              <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>By facility</span>
              <span style={sx('font-size:14px;color:#54606C;')}>Names hidden on this screen</span>
            </div>{' '}
            {!!v.ocFacEmpty && (
              <div style={sx('font-size:16px;color:#54606C;line-height:1.5;')}>
                No facility live yet. Each signed facility appears here with its onboarding: consent collected, devices set up, EHR connected and go-live date.
              </div>
            )}{' '}
            {v.ocByFac.map((f, f_i) => (
              <Fragment key={f_i}>
                <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) 44px 30px 38px;align-items:center;gap:8px;padding:3px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
                  <span style={sx('display:flex;flex-direction:column;gap:1px;min-width:0;')}>
                    <span style={sx('display:flex;align-items:center;gap:6px;')}>
                      <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>
                        <span>{f.n}</span>
                      </span>
                      <span style={sx(f.stS)}>
                        <span>{f.st}</span>
                      </span>
                    </span>
                    <span style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')} title={f.sub}>
                      <span>{f.sub}</span>
                    </span>
                  </span>
                  <span style={sx('font-size:14px;font-weight:700;color:#22272E;text-align:right;font-variant-numeric:tabular-nums;')}>
                    <span>{f.d}</span>
                  </span>
                  <span style={sx('font-size:14px;font-weight:700;color:#22272E;text-align:right;font-variant-numeric:tabular-nums;')} title="Residents enrolled">
                    <span>{f.r}</span>
                  </span>
                  <span style={sx('font-size:14px;font-weight:800;color:#1F6B52;text-align:right;font-variant-numeric:tabular-nums;')} title="Reimbursement collected a month, billed by the practitioner">
                    <span>{f.x}</span>
                  </span>
                </div>
              </Fragment>
            ))}
          </div>
        )}{' '}
        <div style={sx('display:flex;flex-direction:column;gap:7px;min-width:0;')}>
          <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Real results · locked until earned</div>{' '}
          {v.ocLocked.map((l, l_i) => (
            <Fragment key={l_i}>
              <div style={sx('display:flex;gap:8px;')}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#54606C" strokeWidth="1.9" strokeLinecap="round" style={sx('flex:0 0 13px;margin-top:2px;')}>
                  <rect x="5" y="11" width="14" height="10" rx="2" />
                  <path d="M8.5 11V8a3.5 3.5 0 0 1 7 0v3" />
                </svg>
                <span style={sx('display:flex;flex-direction:column;gap:1px;')}>
                  <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>
                    <span>{l.k}</span>
                  </span>
                  <span style={sx('font-size:14px;color:#54606C;line-height:1.45;')}>
                    <span>{l.v}</span>
                  </span>
                </span>
              </div>
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
